<template>
	<div 
		ref="cards"
		class="flow-cards" 
		@scroll="handleScroll">

		<div 
			class="flow-cards__container" 	
			:class="{ 'fullscreen': isFullscreen }">

            <!-- Buffer -->
			<div v-if="!isFullscreen" class="flow-cards__buffer"></div>

            <!-- All Cards -->
			<div v-for="(card, index) in cards" 
				:key="index" 
				:ref="`card-outer-${index}`"
				class="flow-cards__card-outer">

				<component 
					:is="card.component" 
					:key="card.key"
					:app="app"
                    :card="card" 
					:ref="`card-inner-${index}`" 
                    class="flow-cards__card-inner" />

			</div>

            <!-- Buffer -->
			<div v-if="!isFullscreen" class="flow-cards__buffer post"></div>

		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import CardImage from '@/components/flow/cards/CardImage.vue'
import CardImport from '@/components/flow/cards/CardImport.vue'
import CardModel from '@/components/flow/cards/CardModel.vue'
import CardPrompt from '@/components/flow/cards/CardPrompt.vue'
import CardOptimize from '@/components/flow/cards/CardOptimize.vue'
import CardGenerateGeometry from '@/components/flow/cards/CardGenerateGeometry.vue'
import CardGenerateTexture from '@/components/flow/cards/CardGenerateTexture.vue'
import CardSelectAsset from '@/components/flow/cards/CardSelectAsset.vue'
import CardSelectFlow from '@/components/flow/cards/CardSelectFlow.vue'
import CardSelectTexture from '@/components/flow/cards/CardSelectTexture.vue'
import CardWatertight from '@/components/flow/cards/CardWatertight.vue'

export default {
	name: 'Cards',
	components: {
		CardImage,
		CardImport,
		CardModel,
		CardPrompt,
		CardOptimize,
		CardGenerateGeometry,
		CardGenerateTexture,
		CardSelectAsset,
		CardSelectFlow,
		CardSelectTexture,
		CardWatertight
	},
    props: {
        app: {
            type: String,
            required: true
        }
    },
	computed: {
		...mapGetters('flows', [
			'isFullscreen',
			'getCards',
			'getActiveIndex'
		]),
        cards() {
            return this.getCards(this.app)
        },
        activeIndex() {
            return this.getActiveIndex(this.app)
        }
	},
	data() {
		return {
			isAutoScrolling: false,
			autoScrollTimeout: null,
			userScrollTimeout: null,
		}
	},
	watch: {
		activeIndex(newValue, oldValue) {
			if (newValue === oldValue) return
			this.scrollToCard(newValue)
		},
	},
	methods: {
		...mapActions('dialogs', [
			'open'
		]),
		...mapActions('flows', [
			'setActiveIndex',
			'setActiveIndexToLast',
			'setActiveIndexToNext',
		]),

		handleScroll() {
			// if we are auto-scrolling, then ignore
			if (this.isAutoScrolling) return

			// if users scrolling, keep checking in small intervals
			if (this.userScrollTimeout) {
				clearTimeout(this.userScrollTimeout)
			}
			this.userScrollTimeout = setTimeout(() => {
				if (this.isAutoScrolling) return
				this.userScrollTimeout = null
				this.updateActiveCard()
			}, 150)
		},

		/*
		handleKeyboardShortcuts(event) {

			// get the pressed key
			const key = event.key

			if (key === 'Escape') {
				event.preventDefault()
				this.open({
					component: "restart",
					payload: {
						app: this.app
					}
				})
			}
			else if (key === 'ArrowUp') {
				event.preventDefault()
				this.setActiveIndexToLast({
					app: this.app
				})
			} 
			else if (key === 'ArrowDown') {
				event.preventDefault()
				this.setActiveIndexToNext({ 
					app: this.app
				})
			}
		},
		*/

		updateActiveCard() {
			const container = this.$refs.cards
			if (!container) return

			const containerRect = container.getBoundingClientRect()
			const containerCenterY = containerRect.top + containerRect.height / 2

			let closestIndex = 0
			let minDistance = Infinity

			this.cards.forEach((card, index) => {
				const cardEl = this.$refs[`card-outer-${index}`]
				if (!cardEl || !cardEl[0]) return

				const cardRect = cardEl[0].getBoundingClientRect()
				const cardCenterY = cardRect.top + cardRect.height / 2
				const distance = Math.abs(cardCenterY - containerCenterY)
				if (distance < minDistance) {
					minDistance = distance
					closestIndex = index
				}
			})

			if (closestIndex !== this.activeIndex) {
				this.setActiveIndex({
					app: this.app,
					index: closestIndex
				})
			}
			this.$nextTick(() => this.focusActiveCard())
		},
		focusActiveCard() {
			const index = this.activeIndex
			const reference = `card-inner-${index}`

			const card = this.$refs[reference]
			if (card && card[0]) {
				if (typeof card[0].focus === 'function') {
					card[0].focus()
				}
			}
		},
		scrollToCard(index, behavior='smooth') {

			// signal that automatic scrolling is happening
			this.isAutoScrolling = true

			// clear any existing timeout
			if (this.autoScrollTimeout) {
				clearTimeout(this.autoScrollTimeout)
			}

			// actually scroll
			this.$nextTick(() => {
				const cardEl = this.$refs[`card-outer-${index}`]

				if (cardEl && cardEl[0]) {
					cardEl[0].scrollIntoView({
						behavior: behavior,
						block: 'start',
					})
				}
			})

			if (behavior === 'smooth') {
				// after some delay, we consider scrolling done
				this.autoScrollTimeout = setTimeout(() => {
					if (this.activeIndex === index) {
						this.isAutoScrolling = false
					}
				}, 300)
			}
			else {
				this.isAutoScrolling = false
			}
		}
	},

	mounted() {
		this.scrollToCard(this.activeIndex, 'auto')
		//window.addEventListener('keydown', this.handleKeyboardShortcuts)
	},
	beforeUnmount() {
        //window.removeEventListener('keydown', this.handleKeyboardShortcuts)
    }
}
</script>

<style scoped>
.flow-cards {
	display: block;
	width: 100%;
	height: 100vh;
	overflow-y: auto;
	scroll-snap-type: y mandatory;
	box-sizing: border-box;
}

.flow-cards__container {
	display: flex;
	flex-direction: column;
	gap: 5vh;
	align-items: center;
	justify-content: center;
	width: calc(100% - 140px);
	margin-left: 70px;
}

.flow-cards__container.fullscreen {
	width: 100%;
	margin-left: 0px;
}

.flow-cards__buffer {
	display: block;
	width: 100%;
	height: 10vh;
}

.flow-cards__card-outer {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	scroll-snap-align: center;
}

.flow-cards__card-inner {
    width: 100%;
}

@media (max-width: 1600px) {
	.flow-cards__container {
		width: calc(100% - 120px);
		margin-left: 60px;
	}
}

@media (max-width: 1200px) {
	.flow-cards__container {
		width: calc(100% - 100px);
		margin-left: 50px;
	}
}

@media (max-width: 1000px)
{
	.flow-cards__container {
		width: calc(100% - 10px);
		margin-left: 5px;
		margin-right: 5px;
	}

	.flow-cards__container.fullscreen {
		width: 100%;
		margin-left: 0px;
		margin-right: 0px;
	}
}

@media (max-width: 800px) {
	.flow-cards__container {
		width: 100%;
		margin-left: 0px;
		margin-right: 0px;
	}

	.flow-cards__buffer {
		height: 0vh !important;
	}

	.flow-cards__buffer.post {
		height: 30vh !important;
	}

	.flow-cards__card-outer {
		scroll-snap-align: start;
	}
}
</style>